import ShippingOrderRequiredInfoModal from "components/modal/shippingOrderRequiredInfo";
import InlineBtn from "components/shared/inlineBtn";
import ShippingOrderSection from "components/shared/shippingOrderSection";
import { shippingOrderStatusesEnum } from "helpers/enums";
import {
    canDeclineShippingOrderResponseTitle,
    canSignShippingOrderResponseTitle,
    getShippingOrderResponseTitle,
    isShippingOrderDeclined,
} from "helpers/shippingOrder";
import React, { useCallback, useEffect, useState } from "react";
import {
    getByDistributionBidId,
    getInfoToGenerateResponseTitle,
    getItem as getShippingOrder,
} from "sources/shippingOrders";

const hasRequiredInfoIssues = (value) => {
    if (!value || typeof value !== "object") {
        return false;
    }
    if (
        value.hasUnfilledRequiredProperties ||
        value.errorMessage ||
        value.errorMessages?.length ||
        Object.keys(value.propertyErrorMessages || {}).length
    ) {
        return true;
    }
    return Object.values(value).some((item) =>
        Array.isArray(item) ? item.some(hasRequiredInfoIssues) : hasRequiredInfoIssues(item),
    );
};

const BidShippingOrder = ({
    bid,
    modal,
    getItem,
    powerOfAttorneys,
    canSignShippingOrderTitle,
    canStartBid,
    startBid,
    roles = null,
}) => {
    const [shippingOrder, setShippingOrder] = useState(bid.shippingOrder || null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingRequiredInfo, setIsCheckingRequiredInfo] = useState(false);

    const loadShippingOrder = useCallback(() => {
        const shippingOrderRequest = bid.shippingOrder?.id
            ? getShippingOrder(bid.shippingOrder.id)
            : bid.distributionBidId
              ? getByDistributionBidId(bid.distributionBidId)
              : null;

        if (!shippingOrderRequest) {
            setShippingOrder(null);
            return Promise.resolve(null);
        }

        setIsLoading(true);
        return shippingOrderRequest
            .then((data) => {
                const detailedShippingOrder = data || bid.shippingOrder || null;
                setShippingOrder(detailedShippingOrder);
                return detailedShippingOrder;
            })
            .catch(() => {
                const fallbackShippingOrder = bid.shippingOrder || null;
                setShippingOrder(fallbackShippingOrder);
                return fallbackShippingOrder;
            })
            .finally(() => setIsLoading(false));
    }, [bid.distributionBidId, bid.shippingOrder]);

    useEffect(() => {
        loadShippingOrder();
    }, [loadShippingOrder]);

    if (!shippingOrder) {
        return null;
    }

    const responseTitle = getShippingOrderResponseTitle(shippingOrder);
    const declined = isShippingOrderDeclined(shippingOrder);
    const failedToGenerate = shippingOrder.status === shippingOrderStatusesEnum.FailedToGenerateResponseTitle;
    const failedToSend = shippingOrder.status === shippingOrderStatusesEnum.FailedToSendResponseTitle;
    const hasDriver = Boolean(bid.driver?.id);
    const requiresGeneration = !responseTitle?.titleFile?.accessUrlLink;

    const openSignModal = (isDecline = false) => {
        if (
            isDecline
                ? !canDeclineShippingOrderResponseTitle(shippingOrder, canSignShippingOrderTitle)
                : !canSignShippingOrderResponseTitle(shippingOrder, canSignShippingOrderTitle)
        ) {
            return;
        }

        modal({
            name: "signShippingOrderTitle",
            title: isDecline
                ? "Отказ в согласовании ЭЗЗ"
                : failedToSend
                  ? "Повторная отправка ответного титула ЭЗЗ"
                  : responseTitle?.titleFile?.accessUrlLink
                    ? "Подписание ответного титула ЭЗЗ"
                    : "Формирование и подписание ответного титула ЭЗЗ",
            windowMod: "wide",
            bidId: bid.id,
            shippingOrder,
            shippingOrderTitle: responseTitle,
            isDecline,
            powerOfAttorneys,
            retrySend: failedToSend,
            callback: () => Promise.all([loadShippingOrder(), getItem()]).then(([order]) => order),
            onSuccess: isDecline || declined ? undefined : askToStartBid,
        });
    };

    const checkRequiredInfoAndOpenSignModal = () => {
        if (!requiresGeneration) {
            openSignModal();
            return;
        }
        if (!hasDriver || isCheckingRequiredInfo) {
            return;
        }

        setIsCheckingRequiredInfo(true);
        getInfoToGenerateResponseTitle(bid.id)
            .then((info) => {
                if (!info) {
                    return;
                }
                if (hasRequiredInfoIssues(info)) {
                    modal({
                        name: "default",
                        title: "Данные для ответного титула ЭЗЗ",
                        windowMod: "wide",
                        CustomBody: ShippingOrderRequiredInfoModal,
                        bodyData: {
                            data: info,
                            bidId: bid.id,
                            onSuccess: () => {
                                getItem();
                                openSignModal();
                            },
                        },
                    });
                    return;
                }
                openSignModal();
            })
            .catch(() => undefined)
            .finally(() => setIsCheckingRequiredInfo(false));
    };

    const askToStartBid = () => {
        if (!canStartBid || !startBid) {
            return;
        }

        modal({
            name: "confirm",
            title: "Запуск заявки в работу",
            text: "Ответный титул ЭЗЗ подписан. Запустить заявку в работу?",
            buttons: { confirm: "Запустить в работу", cancel: "Оставить в черновике" },
            action: startBid,
        });
    };

    const renderActions = () => {
        const canSign = canSignShippingOrderResponseTitle(shippingOrder, canSignShippingOrderTitle);
        const canDecline = canDeclineShippingOrderResponseTitle(shippingOrder, canSignShippingOrderTitle);

        if (!canSign && !canDecline) {
            return null;
        }

        return (
            <div className="d-flex align-items-center flex-wrap justify-content-end mt-1">
                {!declined && requiresGeneration && !hasDriver && (
                    <div className="mb-1 text-right">
                        <small className="text-warning mr-1 mb-1">У заявки не назначен водитель</small>
                    </div>
                )}
                {canSign && (!declined || responseTitle?.titleFile?.accessUrlLink) && (
                    <InlineBtn
                        value={
                            failedToSend
                                ? "Повторить отправку T2"
                                : responseTitle?.titleFile?.accessUrlLink
                                  ? "Подписать T2"
                                  : failedToGenerate
                                    ? "Повторить формирование и подписание T2"
                                    : "Сформировать и подписать T2"
                        }
                        cname="usb-key ml-2 my-1"
                        action={checkRequiredInfoAndOpenSignModal}
                        disabled={isLoading || isCheckingRequiredInfo || (requiresGeneration && !hasDriver)}
                    />
                )}
                {canDecline && (
                    <button
                        type="button"
                        className="btn btn-xs btn-outline-danger ml-2 my-1"
                        onClick={() => openSignModal(true)}
                        disabled={isLoading || isCheckingRequiredInfo}
                    >
                        Отказать в согласовании
                    </button>
                )}
            </div>
        );
    };

    return (
        <ShippingOrderSection
            shippingOrder={shippingOrder}
            renderActions={renderActions}
            defaultCollapsed={true}
            roles={roles}
        />
    );
};

export default BidShippingOrder;
